import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { POSITION_SLOT_EMPLOYEE_VALIDATION } from '../constants';
import type { PreviewPositionSlotListItem } from '../types';
import usePositionSlotValidationService from '../usePositionSlotValidationService';

type PositionSlotValidationContextValue = {
  errorsByEmployeeAlias: Record<string, string[]>;
};

const PositionSlotValidationContext = createContext<PositionSlotValidationContextValue>({
  errorsByEmployeeAlias: {},
});

export const usePositionSlotValidationErrors = (employeeAlias: string | null | undefined): string[] => {
  const { errorsByEmployeeAlias } = useContext(PositionSlotValidationContext);

  return employeeAlias ? (errorsByEmployeeAlias[employeeAlias] ?? []) : [];
};

const buildEmployeeToPositionSlotMap = (positionSlots: PreviewPositionSlotListItem[]): Record<string, string> =>
  positionSlots.reduce<Record<string, string>>((employeeToPositionSlot, positionSlot) => {
    if (positionSlot.employeeAlias) {
      employeeToPositionSlot[positionSlot.employeeAlias] = positionSlot.id;
    }

    return employeeToPositionSlot;
  }, {});

const buildValidationKey = (positionSlots: PreviewPositionSlotListItem[]): string =>
  positionSlots
    .filter(positionSlot => positionSlot.employeeAlias)
    .map(positionSlot => `${positionSlot.id}:${positionSlot.employeeAlias}`)
    .join('|');

const mapValidationErrors = (validationResponse: Record<string, { errors?: string[] }>): Record<string, string[]> =>
  Object.entries(validationResponse).reduce<Record<string, string[]>>(
    (errorsByEmployeeAlias, [employeeAlias, result]) => {
      if (result.errors?.length) {
        errorsByEmployeeAlias[employeeAlias] = result.errors;
      }

      return errorsByEmployeeAlias;
    },
    {},
  );

type PositionSlotValidationProviderProps = {
  positionSlots: PreviewPositionSlotListItem[];
  loading: boolean;
  children: ReactNode;
};

export const PositionSlotValidationProvider = ({
  positionSlots,
  loading,
  children,
}: PositionSlotValidationProviderProps) => {
  const { isGranted } = useAuthorization();
  const { validateEmployeePositionSlots } = usePositionSlotValidationService();
  const [errorsByEmployeeAlias, setErrorsByEmployeeAlias] = useState<Record<string, string[]>>({});
  const lastFetchedRequestKeyRef = useRef<string | null>(null);
  const validateEmployeePositionSlotsRef = useRef(validateEmployeePositionSlots);
  const canValidate = isGranted(POSITION_SLOT_EMPLOYEE_VALIDATION);

  validateEmployeePositionSlotsRef.current = validateEmployeePositionSlots;

  const employeeToPositionSlot = useMemo(() => buildEmployeeToPositionSlotMap(positionSlots), [positionSlots]);
  const validationRequestKey = useMemo(() => buildValidationKey(positionSlots), [positionSlots]);

  useEffect(() => {
    if (loading) {
      lastFetchedRequestKeyRef.current = null;
      return;
    }

    if (!canValidate || validationRequestKey === lastFetchedRequestKeyRef.current) {
      return;
    }

    lastFetchedRequestKeyRef.current = validationRequestKey;

    if (Object.keys(employeeToPositionSlot).length === 0) {
      setErrorsByEmployeeAlias({});
      return;
    }

    let isCancelled = false;

    validateEmployeePositionSlotsRef
      .current(employeeToPositionSlot)
      .then(validationResponse => {
        if (!isCancelled) {
          setErrorsByEmployeeAlias(mapValidationErrors(validationResponse));
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setErrorsByEmployeeAlias({});
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [canValidate, employeeToPositionSlot, loading, validationRequestKey]);

  const contextValue = useMemo(() => ({ errorsByEmployeeAlias }), [errorsByEmployeeAlias]);

  return (
    <PositionSlotValidationContext.Provider value={contextValue}>{children}</PositionSlotValidationContext.Provider>
  );
};
